import { lazy, Suspense, type ReactElement } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router";
import { Shell } from "./components/Shell";
import type { Me } from "./lib/api";
import { pendingInvite, SessionProvider, useSession } from "./lib/session";
import { ApplicationPage } from "./pages/ApplicationPage";
import { CandidatePage } from "./pages/CandidatePage";
import { Candidates } from "./pages/Candidates";
import { Pools } from "./pages/Pools";
import { PoolPage } from "./pages/PoolPage";
import { Companies } from "./pages/Companies";
import { CompanyPage } from "./pages/CompanyPage";
import { Join } from "./pages/Join";
import { Legal } from "./pages/Legal";
import { Privacy } from "./pages/Privacy";
import { JobPage } from "./pages/JobPage";
import { Jobs } from "./pages/Jobs";
import { Appearance } from "./pages/Appearance";
import { BrowserExtension } from "./pages/BrowserExtension";
import { CalendarFeed } from "./pages/CalendarFeed";
import { Reports } from "./pages/Reports";
import { ClientShortlist } from "./pages/ClientShortlist";
import { PublicNotice } from "./pages/PublicNotice";
import { EmailLogging } from "./pages/EmailLogging";
import { Approvals } from "./pages/Approvals";
import { AwaitingApproval } from "./pages/AwaitingApproval";
import { Billing } from "./pages/Billing";
import { Login } from "./pages/Login";
import { ResetPassword } from "./pages/ResetPassword";
import { Security } from "./pages/Security";
import { Setup } from "./pages/Setup";
import { Signup } from "./pages/Signup";
import { Team } from "./pages/Team";
import { Today } from "./pages/Today";
import { NotFound } from "./pages/NotFound";
import { Titled } from "./lib/title";

// The import page pulls in the spreadsheet parsers, so it is loaded only when opened.
const ImportPage = lazy(() => import("./pages/ImportPage").then((m) => ({ default: m.ImportPage })));

function Splash({ message = "Loading…" }: { message?: string }) {
  return (
    <main className="page" aria-live="polite">
      <p className="label">{message}</p>
    </main>
  );
}

/** Member-only pages. Everyone else is sent where they can make progress. */
function Member({ page }: { page: (me: Me) => ReactElement }) {
  const { session, refresh } = useSession();
  const location = useLocation();
  switch (session.status) {
    case "loading":
      return <Splash />;
    case "error":
      return (
        <main className="page" aria-live="polite">
          <p className="notice" role="alert">
            Couldn't load your account. {session.message}
          </p>
          <p className="spaced-top">
            <button type="button" className="btn secondary" onClick={() => void refresh()}>
              Try again
            </button>
          </p>
        </main>
      );
    case "signedOut":
      return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
    case "noAccount":
      return <Navigate to={pendingInvite.get() ? "/join" : "/setup"} replace />;
    case "member":
      return session.me.tenant.approval === "approved" ? page(session.me) : <AwaitingApproval me={session.me} />;
  }
}

/** Log-in and sign-up pages: skip them when already logged in. */
function Guest({ children }: { children: ReactElement }) {
  const { session } = useSession();
  if (session.status === "loading") return <Splash />;
  if (session.status === "member") return <Navigate to="/today" replace />;
  if (session.status === "noAccount") return <Navigate to={pendingInvite.get() ? "/join" : "/setup"} replace />;
  return children;
}

function NoAccountYet({ children }: { children: ReactElement }) {
  const { session } = useSession();
  if (session.status === "loading") return <Splash />;
  if (session.status === "signedOut") return <Navigate to="/login" replace />;
  if (session.status === "member") return <Navigate to="/today" replace />;
  return children;
}

export function App() {
  return (
    <BrowserRouter>
      <SessionProvider>
        <Routes>
          <Route path="/login" element={<Titled title="Log in"><Guest children={<Login />} /></Titled>} />
          <Route path="/signup" element={<Titled title="Sign up"><Guest children={<Signup />} /></Titled>} />
          <Route path="/reset-password" element={<Titled title="Reset password"><Guest children={<ResetPassword />} /></Titled>} />
          <Route path="/join" element={<Titled title="Join your team"><Join /></Titled>} />
          <Route path="/legal/:doc" element={<Legal />} />
          <Route path="/shortlist" element={<ClientShortlist />} />
          <Route path="/notice/:tenantId" element={<PublicNotice />} />
          <Route path="/setup" element={<Titled title="Set up your agency"><NoAccountYet children={<Setup />} /></Titled>} />
          <Route element={<Shell />}>
            <Route path="/today" element={<Titled title="Today"><Member page={(me) => <Today me={me} />} /></Titled>} />
            <Route path="/companies" element={<Titled title="Companies"><Member page={(me) => <Companies me={me} />} /></Titled>} />
            <Route path="/companies/:id" element={<Member page={(me) => <CompanyPage me={me} />} />} />
            <Route path="/jobs" element={<Titled title="Jobs"><Member page={(me) => <Jobs me={me} />} /></Titled>} />
            <Route path="/jobs/:id" element={<Member page={() => <JobPage />} />} />
            <Route path="/candidates" element={<Titled title="Candidates"><Member page={() => <Candidates />} /></Titled>} />
            <Route path="/candidates/:id" element={<Member page={() => <CandidatePage />} />} />
            <Route path="/pools" element={<Titled title="Talent pools"><Member page={() => <Pools />} /></Titled>} />
            <Route path="/pools/:id" element={<Member page={() => <PoolPage />} />} />
            <Route path="/applications/:id" element={<Member page={() => <ApplicationPage />} />} />
            <Route path="/import" element={<Titled title="Import"><Member page={() => <Suspense fallback={<Splash />}><ImportPage /></Suspense>} /></Titled>} />
            <Route path="/reports" element={<Titled title="Reports"><Member page={() => <Reports />} /></Titled>} />
            <Route path="/team" element={<Titled title="Team"><Member page={(me) => <Team me={me} />} /></Titled>} />
            <Route path="/privacy" element={<Titled title="Privacy"><Member page={(me) => <Privacy me={me} />} /></Titled>} />
            <Route path="/security" element={<Titled title="Account"><Member page={(me) => <Security me={me} />} /></Titled>} />
            <Route path="/calendar" element={<Titled title="Calendar"><Member page={() => <CalendarFeed />} /></Titled>} />
            <Route path="/email-log" element={<Titled title="Email logging"><Member page={() => <EmailLogging />} /></Titled>} />
            <Route path="/extension" element={<Titled title="Browser extension"><Member page={() => <BrowserExtension />} /></Titled>} />
            <Route path="/appearance" element={<Titled title="Appearance"><Member page={() => <Appearance />} /></Titled>} />
            <Route path="/billing" element={<Titled title="Billing"><Member page={(me) => <Billing me={me} />} /></Titled>} />
            <Route path="/approvals" element={<Titled title="Approvals"><Member page={(me) => <Approvals me={me} />} /></Titled>} />
            <Route path="*" element={<Titled title="Not found"><Member page={() => <NotFound />} /></Titled>} />
          </Route>
          <Route path="/" element={<Navigate to="/today" replace />} />
        </Routes>
      </SessionProvider>
    </BrowserRouter>
  );
}
